/**
 * BirthdaysWidget — Dashboard-Karte mit kommenden Geburtstagen.
 * - Heute: Hervorgehoben mit Cake-Icon + grünem Badge
 * - Naechste 14 Tage: Datum + "in X Tagen"
 * - Standort-gefiltert (RBAC backend-seitig)
 * - DSGVO: zeigt nur Tag.Monat, kein Geburtsjahr/Alter.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Cake, Gift } from 'lucide-react';
import api from '../../lib/api';

const daysLabel = (n) => {
  if (n === 0) return 'Heute';
  if (n === 1) return 'Morgen';
  return `in ${n} Tagen`;
};

export const BirthdaysWidget = () => {
  const [birthdays, setBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get('/dashboard/birthdays?days=14')
      .then((res) => {
        if (mounted) setBirthdays(res.data?.birthdays || []);
      })
      .catch(() => mounted && setBirthdays([]))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <Card className="border-border/60">
        <CardHeader className="border-b border-border/40 pb-4">
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (birthdays.length === 0) return null;

  const todayBirthdays = birthdays.filter((b) => b.is_today);
  const upcoming = birthdays.filter((b) => !b.is_today);

  return (
    <Card className="border-border/60" data-testid="birthdays-widget">
      <CardHeader className="border-b border-border/40 pb-4">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Cake className="h-5 w-5 text-pink-500" />
          Geburtstage
          {todayBirthdays.length > 0 && (
            <Badge className="ml-1 bg-pink-500 hover:bg-pink-600 text-white">
              {todayBirthdays.length} heute
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-2">
        {todayBirthdays.length > 0 && (
          <div className="px-3 py-3 mb-2 rounded-lg bg-pink-50 dark:bg-pink-950/20 border border-pink-200 dark:border-pink-900">
            {todayBirthdays.map((b) => (
              <Link
                key={b.id}
                to={`/employees/${b.id}`}
                className="flex items-center gap-3 py-1.5 hover:opacity-80 transition-opacity"
                data-testid={`birthday-today-${b.id}`}
              >
                <Gift className="h-5 w-5 text-pink-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{b.name}</p>
                  {b.position && (
                    <p className="text-xs text-muted-foreground truncate">{b.position}</p>
                  )}
                </div>
                <Badge className="bg-pink-500 hover:bg-pink-600 text-white">Heute</Badge>
              </Link>
            ))}
          </div>
        )}

        {upcoming.length > 0 && (
          <div className="divide-y divide-border/40">
            {upcoming.map((b) => (
              <Link
                key={b.id}
                to={`/employees/${b.id}`}
                className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50 transition-colors group"
                data-testid={`birthday-${b.id}`}
              >
                <div className="text-center w-12 shrink-0">
                  <p className="text-xs text-muted-foreground">{b.date_label}</p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                    {b.name}
                  </p>
                  {b.position && (
                    <p className="text-xs text-muted-foreground truncate">{b.position}</p>
                  )}
                </div>
                <Badge variant="outline" className="text-xs shrink-0">
                  {daysLabel(b.days_until)}
                </Badge>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
